/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * publishApi — declaring a podium, publishing it, and taking it back.
 *
 * Two stages, deliberately not one:
 *
 *   DECLARE  marks 1, 2 or 3 against result rows. A working draft. Nothing
 *            public reads it, so it can be wrong and changed again.
 *   PUBLISH  copies those rows into the award table, which is the public
 *            record. Nothing unpublished can leak, because an unpublished
 *            winner does not exist in that table at all — absence is a far
 *            stronger guarantee than a flag somebody can misconfigure.
 *
 * UNPUBLISH HIDES, IT DOES NOT ERASE. The award rows STAY, and the race's
 * results_published flag is what takes the podium off the board. What was
 * announced survives, which also lets "never published" be told apart from
 * "published, then withdrawn" — two states an empty table cannot distinguish.
 * The draft is left untouched, or correcting one winner would mean picking all
 * three again.
 *
 * WHY THE ROWS STAY — the republish bug, fixed 2026-11-02.
 * Unpublish used to soft-delete the awards. The award table's uniqueness rule
 * (event_activity_id, position, category) is a plain constraint that still
 * counts deleted rows, so publishing a second time collided with a first place
 * nobody could see, and a race that had been unpublished could never be
 * published again. The platform cannot express "unique among live rows only" —
 * it builds plain constraints and plain unique indexes with no condition — and
 * it hides soft-deleted rows from reads, so the old row could not be found and
 * reused either. Keeping the rows and publishing IN PLACE removes the collision
 * at its source.
 *
 * THE TRADE, stated plainly: an award that has been withdrawn stays readable
 * through the public API. The board hides it (LeaderboardPage checks
 * results_published before drawing the podium) but a direct API call would see
 * it. This was the owner's decision of 2026-11-02, taken knowingly, because the
 * alternative needed a database capability that does not exist.
 */

import envConfig from '../../../config/envConfig';
import { RESULT_TABLE } from './resultsApi';
import { awardRowsFor, rankedPositions, publishBlocker } from './publishModel';
import { fetchFieldSummary } from './boardApi';

export const AWARD_TABLE = 'event_activity_result_award_summary';
export const RACE_TABLE = 'event_activity';
export const ORGANIZER_PAGE_SIZE = 500;

function scope() {
  return {
    organization_id: Number(envConfig.organizationId),
    application_id: Number(envConfig.applicationId),
  };
}

async function readError(resp, fallback) {
  try {
    const body = await resp.json();
    const message = body && (body.error || body.detail || body.message);
    if (message) return Array.isArray(message) ? message.join(' ') : String(message);
  } catch (e) { /* fall through */ }
  return fallback;
}

async function fetchRows(api, body, { signal, errorMessage } = {}) {
  const resp = await api.apiCall('/arcee/application/table/fetch/', {
    method: 'POST', signal, body: JSON.stringify({ ...scope(), ...body }),
  });
  if (!resp.ok) throw new Error(errorMessage || 'Could not read that.');
  const payload = await resp.json();
  const rows = Array.isArray(payload) ? payload : (payload?.records || payload?.results || []);
  return { rows, count: typeof payload?.count === 'number' ? payload.count : rows.length };
}

async function saveRow(api, tableName, payload, errorMessage) {
  const resp = await api.apiCall('/arcee/application/table/save/record/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...scope(), table_name: tableName, payload }),
  });
  if (!resp.ok) throw new Error(await readError(resp, errorMessage));
  return resp.json().catch(() => ({}));
}

// `errorMessage` defaults to the award wording so that every existing caller
// reads exactly as it did; only the new result-removal path passes its own.
async function deleteRow(api, tableName, id, errorMessage = 'Could not withdraw an award.') {
  const resp = await api.apiCall('/arcee/application/table/delete-record/', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...scope(), table_name: tableName, id }),
  });
  if (!resp.ok) {
    const err = new Error(await readError(resp, errorMessage));
    err.status = resp.status;   // lets a bulk remove tell a 429 from a refusal
    throw err;
  }
}

/**
 * Whether this race's winners are currently published.
 *
 * Needed because award ROWS are not the answer. `unpublishResults` leaves them
 * in place on purpose — deleting them is what made republishing impossible —
 * so "an award exists" stays true after an Unpublish. Only this flag says
 * whether anything is on the public podium right now.
 *
 * READ THROUGH THE BOARD'S SUMMARY QUERY, not a table read. The obvious
 * version of this filtered `event_activity` on `id` and the API refused it
 * outright — "Column 'id' not found in this table" — because the filter layer
 * does not expose a table's primary key as a filterable column. It failed
 * quietly through React Query, the flag read as false, and every published
 * winner lost its guard. Measured 2026-11-03.
 *
 * The summary query already carries `results_published` (it is what the public
 * board gates its podium on), takes the race id as a parameter, and is public,
 * so it needs no session and cannot be narrowed by row-level security.
 */
export async function fetchRacePublished(api, { eventActivityId, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return false;
  const summary = await fetchFieldSummary(api, { eventActivityId, signal });
  return Boolean(summary?.results_published);
}

/**
 * Detaches one result, putting its athlete back on the attach panel.
 *
 * WHY THIS IS A DELETE AND NOT AN EDIT. There is nothing to edit: a result's
 * link to a run is `activity_id`, and re-pointing it would leave the time,
 * distance and timing source belonging to the old run until every one of them
 * was rewritten too. Deleting the row says the honest thing — this attach was
 * a mistake — and the attach panel then offers the athlete again with all of
 * their runs, because its rule is "entrants with no result yet". The run that
 * was wrongly taken frees up at the same time, for the same reason.
 *
 * THE DATABASE WILL NOT SAVE YOU FROM A PUBLISHED WINNER. This looks like it
 * should be safe — an award points at the result with no cascade — but
 * `event_result` is SOFT deleted. `deleted_at` is set, the foreign key is
 * never tested, and the award survives pointing at a row that reads as gone.
 * The podium then shows that winner with "Time not available" for good.
 * Measured on 2026-11-03, after this removed a published champion cleanly.
 *
 * So the caller MUST refuse this for a placed athlete before calling, and ask
 * them to Unpublish first. There is no constraint error to catch and translate
 * — nothing fails. See `publishedResultIds` in PublishResultsPage.
 *
 * THE REGISTRATION IS FREED, as of pack 1.0.12. `registration_id`'s uniqueness
 * is now a CONDITIONAL index — unique among rows with `deleted_at IS NULL` —
 * so a removed result stops blocking its registration and the athlete can be
 * attached again. Under the old plain constraint they could not: the attach
 * panel offered them, the insert was refused silently inside a batch, and one
 * such athlete failed an entire CSV import. Soft delete is kept, so the removed
 * row is still there for audit.
 */
export async function removeResult(api, { resultId } = {}) {
  if (resultId === null || resultId === undefined) return;
  await deleteRow(api, RESULT_TABLE, resultId, 'Could not remove that result.');
}

/** Removals in flight at once when taking many results off a race. */
export const REMOVE_CONCURRENCY = 4;
/** Waits after a 429 on one removal before trying it again. */
export const REMOVE_THROTTLE_WAITS_MS = [1500, 3000, 6000, 12000];

/**
 * Takes many results off a race — the whole field, if asked.
 *
 * The platform deletes one record per request (with its row-level check and
 * pre-delete hook), so 20,000 results are 20,000 requests. One at a time that
 * is most of an hour; REMOVE_CONCURRENCY at once keeps the server's pool busy
 * without flooding it. A 429 waits and tries the SAME result again; any other
 * refusal is reported against that result and the rest carry on.
 *
 * `picks` are { id, name }. `onProgress(done, total)` after every one.
 * Answers { removed: [id strings], refused: [{ name, reason }] }.
 */
export async function removeResults(api, picks, {
  concurrency = REMOVE_CONCURRENCY, onProgress, waits = REMOVE_THROTTLE_WAITS_MS,
} = {}) {
  const removed = [];
  const refused = [];
  let next = 0;
  let done = 0;
  const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

  async function one(pick) {
    for (let attempt = 0; ; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await removeResult(api, { resultId: pick.id });
        removed.push(String(pick.id));
        return;
      } catch (e) {
        if (e && e.status === 429 && attempt < waits.length) {
          // eslint-disable-next-line no-await-in-loop
          await pause(waits[attempt]);
        } else {
          refused.push({ name: pick.name, reason: (e && e.message) || 'Could not remove that result.' });
          return;
        }
      }
    }
  }

  async function worker() {
    while (next < picks.length) {
      const pick = picks[next];
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      await one(pick);
      done += 1;
      if (onProgress) onProgress(done, picks.length);
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, picks.length) }, worker));
  return { removed, refused };
}

/** Every result for a race, read with the organizer's session so drafts show. */
export function fetchResultsForRace(api, { eventActivityId, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) {
    return Promise.resolve({ rows: [], count: 0 });
  }
  return fetchRows(api, {
    table_name: RESULT_TABLE,
    filters: [{ column_key: 'event_activity_id', filter_type: 'equals', column_value: eventActivityId }],
    page: 1,
    page_size: ORGANIZER_PAGE_SIZE,
  }, { signal, errorMessage: 'Could not load the results for this race.' });
}

/** The awards currently published for a race. */
export function fetchAwards(api, { eventActivityId, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) {
    return Promise.resolve({ rows: [], count: 0 });
  }
  return fetchRows(api, {
    table_name: AWARD_TABLE,
    filters: [{ column_key: 'event_activity_id', filter_type: 'equals', column_value: eventActivityId }],
    page: 1,
    page_size: ORGANIZER_PAGE_SIZE,
  }, { signal, errorMessage: 'Could not load the published winners.' });
}

/**
 * The pair an award is unique on: its placing within the race.
 *
 * Built from position + category because that is exactly what the table's
 * uniqueness rule uses. Category is never empty by the time it gets here
 * (publishModel writes 'Overall'), but the fallback keeps this honest if a row
 * written before that rule existed turns up.
 */
function placingKey(award) {
  return `${Number(award.position)}::${String(award.category || 'Overall')}`;
}

/** Marks, or clears, one athlete's draft podium place. */
export function setDeclaredPosition(api, { resultId, position }) {
  return saveRow(api, RESULT_TABLE, {
    id: resultId,
    declared_position: position === null || position === '' ? null : Number(position),
  }, 'Could not mark that place.');
}

function setPublishedFlag(api, eventActivityId, published) {
  return saveRow(api, RACE_TABLE, { id: eventActivityId, results_published: published },
    'Could not update the race.');
}

/**
 * Publishes a race's declared podium.
 *
 * Order matters. Positions are worked out and saved FIRST, because this is the
 * moment the finishing order becomes official — doing it on every result save
 * instead would mean editing the hook slot that currently sends certificate
 * emails.
 *
 * Then the awards are brought into line IN PLACE, matched on their placing
 * (position + category), which is exactly the pair the table's uniqueness rule
 * is built from:
 *
 *   same placing, already there  ->  UPDATE it (the winner may have changed)
 *   new placing                  ->  INSERT it
 *   placing no longer awarded    ->  remove it
 *
 * Pressing publish twice therefore leaves three winners, not six, and leaves
 * the SAME three rows rather than a new set — which is what makes republishing
 * after an unpublish work at all. See the header.
 *
 * KNOWN EDGE, and it is narrow: removing a placing still soft-deletes that row,
 * so shrinking a podium from three winners to two and later growing it back to
 * three would hit the old collision on the third place. Shrinking a podium is
 * rare and the common path — publish, unpublish, publish again — no longer
 * touches a delete at all. Flagged rather than hidden.
 */
export async function publishResults(api, { eventActivityId, activityName, eventName, results }) {
  const blocker = publishBlocker(results);
  if (blocker) throw new Error(blocker);

  for (const ranked of rankedPositions(results)) {
    await saveRow(api, RESULT_TABLE, { id: ranked.id, position_overall: ranked.position_overall },
      'Could not save the finishing order.');
  }

  const existing = await fetchAwards(api, { eventActivityId });
  const byPlacing = new Map();
  existing.rows.forEach((award) => { byPlacing.set(placingKey(award), award); });

  const rows = awardRowsFor({ results, eventActivityId, activityName, eventName });
  const wanted = new Set(rows.map(placingKey));

  for (const row of rows) {
    const already = byPlacing.get(placingKey(row));
    await saveRow(
      api, AWARD_TABLE,
      already ? { ...row, id: already.id } : row,
      'Could not publish a winner.',
    );
  }

  // A placing that is no longer awarded. See the KNOWN EDGE note above.
  for (const award of existing.rows) {
    if (!wanted.has(placingKey(award))) {
      await deleteRow(api, AWARD_TABLE, award.id);
    }
  }

  await setPublishedFlag(api, eventActivityId, true);
  return rows.length;
}

/**
 * Takes a race's results back off the board.
 *
 * Lowers the flag and nothing else. The award rows stay exactly where they are,
 * which is what lets the race be published again — see the header's note on the
 * republish bug. The draft is left exactly as it was too, so a mistake can be
 * corrected and published again without choosing every winner from scratch.
 *
 * Returns how many awards were taken off the board, which is what the organizer
 * screen reports back.
 */
export async function unpublishResults(api, { eventActivityId }) {
  const existing = await fetchAwards(api, { eventActivityId });
  // No deleteRow here, deliberately. Commented-out history: this used to be
  //   for (const award of existing.rows) await deleteRow(api, AWARD_TABLE, award.id);
  // which is precisely what made republishing impossible.
  await setPublishedFlag(api, eventActivityId, false);
  return existing.rows.length;
}

/**
 * The races this organizer can publish results for.
 *
 * No owner filter is passed: the platform's row-level security already scopes
 * event_activity to what this organizer owns through its event, so filtering
 * here as well would be a second, weaker copy of the same rule.
 */
export function fetchMyRaces(api, { signal } = {}) {
  return fetchRows(api, {
    table_name: RACE_TABLE,
    ordering: '-id',
    page: 1,
    page_size: ORGANIZER_PAGE_SIZE,
  }, { signal, errorMessage: 'Could not load your races.' });
}

/** The saved query that answers "do I own this race?". */
export const OWNS_RACE_QUERY = 'organizer_owns_race';

/**
 * Whether the caller created this race. Decided by the SERVER.
 *
 * The query is scoped by {{caller_filter}}, so it answers one row to the race's
 * creator and nothing to anybody else. A non-owner cannot tell that answer from
 * a race that does not exist, which is the right amount to tell them.
 *
 * Not a table read: event_activity is PUBLIC, so reading the row and comparing
 * created_by_id in the browser would answer for every signed-in visitor.
 */
export async function ownsRace(api, { eventActivityId, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return false;
  const resp = await api.apiCall(
    `/arcee/application/${Number(envConfig.applicationId)}/analytics/${OWNS_RACE_QUERY}/run/`
    + `?event_activity_id=${encodeURIComponent(eventActivityId)}`,
    { method: 'GET', signal },
  );
  // A refusal is the ordinary answer for an athlete, not an error to report.
  if (!resp.ok) return false;
  const payload = await resp.json().catch(() => ({}));
  return (payload?.rows || []).length > 0;
}

/**
 * Every event this organizer owns.
 *
 * Fetched alongside the races so the Results page can show an event that has no
 * activities yet. Listing races alone silently hides such an event, and the
 * organizer cannot tell whether it is missing or simply has nothing to publish.
 */
export function fetchMyEvents(api, { signal } = {}) {
  return fetchRows(api, {
    table_name: 'event',
    ordering: '-id',
    page: 1,
    page_size: ORGANIZER_PAGE_SIZE,
  }, { signal, errorMessage: 'Could not load your events.' });
}

/**
 * Races grouped under their event, newest event first, including events that
 * have no races at all.
 */
export function groupRacesByEvent(events, races) {
  const byEvent = new Map();
  (events || []).forEach((event) => {
    byEvent.set(String(event.id), { event, races: [] });
  });
  (races || []).forEach((race) => {
    const raw = race.event_id;
    const id = raw && typeof raw === 'object' ? ((raw.data || {}).id ?? raw.id) : raw;
    const key = String(id);
    // A race whose event is not in the list still has to appear somewhere, or
    // it becomes invisible — the exact failure this function exists to prevent.
    if (!byEvent.has(key)) byEvent.set(key, { event: null, races: [] });
    byEvent.get(key).races.push(race);
  });
  return [...byEvent.values()].sort((a, b) => {
    const ida = Number(a.event ? a.event.id : (a.races[0] || {}).event_id) || 0;
    const idb = Number(b.event ? b.event.id : (b.races[0] || {}).event_id) || 0;
    return idb - ida;
  });
}

export const ORGANIZER_RESULTS_PAGE_QUERY = 'organizer_race_results_page';
export const ORGANIZER_DECLARED_QUERY = 'organizer_race_declared';
/** The same table on a multi-day event: each entrant's runs added up. */
export const ORGANIZER_TOTALS_PAGE_QUERY = 'organizer_race_totals_page';

/** Rows per page of the declare page's table (the saved query's LIMIT). */
export const ORGANIZER_RESULTS_PAGE_SIZE = 100;
/** Waits after a 429 on one page read before asking again. */
export const PAGE_THROTTLE_WAITS_MS = [1500, 3000, 6000];

function organizerRows(payload) {
  const columns = (payload?.columns || []).map((c) => c.name);
  return (payload?.rows || []).map((row) => {
    const record = Object.fromEntries(columns.map((c, i) => [c, row[i]]));
    const { result_id: resultId, ...rest } = record;
    return { id: resultId, ...rest };
  });
}

/**
 * ONE PAGE of the race's results for the declare page, fastest first, with the
 * true total. The whole-race read below is capped at 500 rows by the server,
 * so a 40,000-entrant race showed 500 and could not be paged.
 */
export async function fetchOrganizerResultsPage(api, {
  eventActivityId, page = 1, search = '', multiDay = false, signal,
} = {}) {
  // A multi-day event lists each entrant once with every attached run added
  // up (their own run plus the day-by-day ones), furthest first.
  const queryName = multiDay ? ORGANIZER_TOTALS_PAGE_QUERY : ORGANIZER_RESULTS_PAGE_QUERY;
  const params = new URLSearchParams({
    event_activity_id: eventActivityId,
    search: String(search || '').trim().replace(/[\\%_]/g, (c) => `\\${c}`),
    row_offset: Math.max(0, (Number(page) || 1) - 1) * ORGANIZER_RESULTS_PAGE_SIZE,
  }).toString();
  const url = `/arcee/application/${Number(envConfig.applicationId)}/analytics/${queryName}/run/?${params}`;
  let resp;
  // The analytics throttle (5/sec, 60/min) answers 429 to quick page turns.
  // Wait it out a few times before calling it an error.
  for (let attempt = 0; ; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    resp = await api.apiCall(url, { method: 'GET', signal });
    if (resp.status !== 429 || attempt >= PAGE_THROTTLE_WAITS_MS.length) break;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => { setTimeout(resolve, PAGE_THROTTLE_WAITS_MS[attempt]); });
  }
  if (resp.status === 429) throw new Error('The server is busy. Wait a moment and try again.');
  if (!resp.ok) throw new Error('Could not load the results for this race.');
  const rows = organizerRows(await resp.json());
  return { rows, total: rows.length ? Number(rows[0].total_rows) || 0 : 0 };
}

/**
 * Every result with a place marked, whichever page it is on. Publishing and
 * its checks read these — never one page of the table.
 */
export async function fetchDeclaredResults(api, { eventActivityId, signal } = {}) {
  const resp = await api.apiCall(
    `/arcee/application/${Number(envConfig.applicationId)}/analytics/${ORGANIZER_DECLARED_QUERY}/run/`
    + `?event_activity_id=${encodeURIComponent(eventActivityId)}`,
    { method: 'GET', signal },
  );
  if (!resp.ok) throw new Error('Could not load the places marked for this race.');
  return organizerRows(await resp.json());
}

/** The saved query the declare page reads its rows from. */
export const ORGANIZER_RESULTS_QUERY = 'organizer_race_results';

/**
 * Every result of one race, for the organizer who owns its event.
 *
 * Not a table read: `event_result` is public, and the platform narrows a public
 * read to the viewer's OWN rows whenever they belong to a group. An organizer
 * therefore saw none of their athletes' results and the declare page came up
 * empty. The saved query is scoped by event ownership instead, which is the
 * right test — you may declare winners for a race on an event you own.
 *
 * `result_id` is mapped back to `id` so the rest of this module and the page
 * keep working with ordinary result rows.
 */
export async function fetchOrganizerRaceResults(api, { eventActivityId, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return { rows: [], count: 0 };
  const resp = await api.apiCall(
    `/arcee/application/${Number(envConfig.applicationId)}/analytics/${ORGANIZER_RESULTS_QUERY}/run/`
    + `?event_activity_id=${encodeURIComponent(eventActivityId)}`,
    { method: 'GET', signal },
  );
  if (!resp.ok) throw new Error('Could not load the results for this race.');
  const payload = await resp.json();
  const columns = (payload?.columns || []).map((c) => c.name);
  const rows = (payload?.rows || []).map((row) => {
    const record = Object.fromEntries(columns.map((c, i) => [c, row[i]]));
    const { result_id: resultId, ...rest } = record;
    return { id: resultId, ...rest };
  });
  return { rows, count: rows.length };
}
